import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, addDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { showSuccess, showError, showWarning, showInfo } from '../utils/toast';

// Import exam components
import ExamHeader from '../components/exam/ExamHeader';
import QuestionDisplay from '../components/exam/QuestionDisplay';
import QuestionNavigation from '../components/exam/QuestionNavigation';
import QuestionPalette from '../components/exam/QuestionPalette';
import SubmitConfirmModal from '../components/exam/SubmitConfirmModal';
import WarningModal from '../components/exam/WarningModal';

const TakeExam = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [markedForReview, setMarkedForReview] = useState(new Set());
  const [timeLeft, setTimeLeft] = useState(0);
  const [sectionTimeLeft, setSectionTimeLeft] = useState(0);
  const [questionTimeLeft, setQuestionTimeLeft] = useState(0);
  const [timerType, setTimerType] = useState('main'); // 'main', 'section', or 'question'
  const [questionTimeRemaining, setQuestionTimeRemaining] = useState({}); // Track remaining time per question
  const [expiredQuestions, setExpiredQuestions] = useState(new Set()); // Track questions whose timer expired
  const [loading, setLoading] = useState(true);
  const [submissionId, setSubmissionId] = useState(null);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [visited, setVisited] = useState(new Set());
  // eslint-disable-next-line no-unused-vars
  const [questionDisplayMap, setQuestionDisplayMap] = useState({}); // Maps originalIndex to displayNumber
  const [displayOrder, setDisplayOrder] = useState([]); // Array of originalIndices in display order
  // eslint-disable-next-line no-unused-vars
  const [shuffleMapping, setShuffleMapping] = useState(null); // Stores shuffle information for answer mapping
  
  // New state for security features
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [cameraPermission, setCameraPermission] = useState(null);
  const [locationPermission, setLocationPermission] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  
  const videoRef = useRef(null);
  const submittedRef = useRef(false);
  const answersRef = useRef({}); // Keep track of latest answers for immediate save

  // Seeded random function for consistent shuffling per student
  const seededRandom = (seed) => {
    const x = Math.sin(seed++) * 10000;
    return x - Math.floor(x);
  };

  // Fisher-Yates shuffle with seed
  const shuffleArray = (array, seed) => {
    const shuffled = [...array];
    let currentSeed = seed;
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(seededRandom(currentSeed++) * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Generate seed from studentId
  const generateSeed = (studentId, examId) => {
    let hash = 0;
    const str = `${studentId}_${examId}`;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash);
  };

  // Fetch exam and questions
  useEffect(() => {
    fetchExamData();
    requestPermissions();
    
    // Prevent browser back button after starting exam
    window.history.pushState(null, '', window.location.href);
    const preventBack = (e) => {
      window.history.pushState(null, '', window.location.href);
      if (!submittedRef.current) {
        showWarning('Please submit the exam properly. Do not use browser back button!');
      }
    };
    window.addEventListener('popstate', preventBack);
    
    return () => {
      window.removeEventListener('popstate', preventBack);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId]);

  // Disable inspect, dev tools, and page refresh
  useEffect(() => {
    // Disable right-click
    const disableRightClick = (e) => {
      e.preventDefault();
      showWarning('Right-click is disabled during exam');
      return false;
    };

    // Disable keyboard shortcuts for dev tools and fullscreen exit
    const disableDevTools = (e) => {
      // ESC key - Block fullscreen exit
      if (e.keyCode === 27 && !submittedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        showWarning('You cannot exit fullscreen during the exam!');
        return false;
      }
      // F11 - Full screen toggle
      if (e.keyCode === 122) {
        e.preventDefault();
        return false;
      }
      // F12
      if (e.keyCode === 123) {
        e.preventDefault();
        showWarning('Developer tools are disabled during exam');
        return false;
      }
      // Ctrl+Shift+I
      if (e.ctrlKey && e.shiftKey && e.keyCode === 73) {
        e.preventDefault();
        showWarning('Developer tools are disabled during exam');
        return false;
      }
      // Ctrl+Shift+C
      if (e.ctrlKey && e.shiftKey && e.keyCode === 67) {
        e.preventDefault();
        return false;
      }
      // Ctrl+Shift+J
      if (e.ctrlKey && e.shiftKey && e.keyCode === 74) {
        e.preventDefault();
        return false;
      }
      // Ctrl+U (view source)
      if (e.ctrlKey && e.keyCode === 85) {
        e.preventDefault();
        return false;
      }
      // F5 or Ctrl+R (refresh)
      if (e.keyCode === 116 || (e.ctrlKey && e.keyCode === 82)) {
        e.preventDefault();
        showWarning('Page refresh is disabled during exam');
        return false;
      }
      // Alt+F4 or Alt+Tab notification
      if (e.altKey && (e.keyCode === 115 || e.keyCode === 9)) {
        e.preventDefault();
        showWarning('Please stay in the exam window!');
        return false;
      }
    };

    // Prevent page unload/refresh
    const preventUnload = (e) => {
      if (!submittedRef.current) {
        e.preventDefault();
        e.returnValue = 'Exam in progress! Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    document.addEventListener('contextmenu', disableRightClick);
    document.addEventListener('keydown', disableDevTools);
    window.addEventListener('beforeunload', preventUnload);

    return () => {
      document.removeEventListener('contextmenu', disableRightClick);
      document.removeEventListener('keydown', disableDevTools);
      window.removeEventListener('beforeunload', preventUnload);
    };
  }, []);

  // Request camera, location, and fullscreen permissions
  const requestPermissions = async () => {
    // Check if already in fullscreen mode (should be from instructions page)
    if (document.fullscreenElement) {
      setIsFullscreen(true);
    } else {
      // Try to enter fullscreen if not already (backup)
      try {
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
          setIsFullscreen(true);
          showSuccess('Fullscreen mode activated');
        }
      } catch (error) {
        showWarning('Please enable fullscreen mode for better experience');
      }
    }

    // Request Camera Permission
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraPermission('granted');
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      showInfo('Camera permission granted');
    } catch (error) {
      setCameraPermission('denied');
      showWarning('Camera permission denied - exam will continue');
    }

    // Request Location Permission
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setLocationPermission('granted');
          showInfo('Location permission granted');
        },
        (error) => {
          setLocationPermission('denied');
          showWarning('Location permission denied - exam will continue');
        }
      );
    }
  };

  // Detect tab switching and visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && !submittedRef.current && exam) {
        const newCount = tabSwitchCount + 1;
        setTabSwitchCount(newCount);
        
        const tabShiftMode = exam.tabShiftMode || 'count-only';
        
        if (tabShiftMode === 'auto-submit' && newCount >= 5) {
          setWarningMessage(
            `You have switched tabs 5 times. Your exam will be auto-submitted for security reasons.`
          );
          setShowWarningModal(true);
          setTimeout(() => {
            handleAutoSubmit();
          }, 3000);
        } else if (tabShiftMode === 'auto-submit') {
          setWarningMessage(
            `Warning! Tab switching detected (${newCount}/5). After 5 switches, your exam will be auto-submitted.`
          );
          setShowWarningModal(true);
          setTimeout(() => setShowWarningModal(false), 4000);
        } else {
          // count-only mode
          setWarningMessage(
            `Tab switch detected (${newCount}). This will be recorded in your exam report.`
          );
          setShowWarningModal(true);
          setTimeout(() => setShowWarningModal(false), 3000);
        }
        
        // Save tab switch count
        if (submissionId) {
          updateDoc(doc(db, 'examSubmissions', submissionId), {
            tabSwitchCount: newCount,
            lastTabSwitch: new Date().toISOString()
          }).catch(console.error);
        }
      }
    };

    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isCurrentlyFullscreen);
      
      if (!isCurrentlyFullscreen && !submittedRef.current) {
        setWarningMessage('Fullscreen mode is mandatory! Re-entering fullscreen...');
        setShowWarningModal(true);
        
        // Force fullscreen immediately - no delay
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
          elem.requestFullscreen().catch((error) => {
            console.error('Failed to re-enter fullscreen:', error);
            showError('Fullscreen mode is required to continue the exam!');
          });
        }
        
        // Close warning after 2 seconds
        setTimeout(() => setShowWarningModal(false), 2000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabSwitchCount, submissionId, exam]);

  // Continuous fullscreen monitor - Force fullscreen every 500ms
  useEffect(() => {
    const fullscreenMonitor = setInterval(() => {
      if (!submittedRef.current && !document.fullscreenElement) {
        // User exited fullscreen - force re-entry immediately
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
          elem.requestFullscreen().catch((error) => {
            console.error('Fullscreen re-entry failed:', error);
          });
        }
      }
    }, 500); // Check every 500ms

    return () => clearInterval(fullscreenMonitor);
  }, []);

  // Additional ESC key blocker at document level
  useEffect(() => {
    const blockEscKey = (e) => {
      if (e.key === 'Escape' && !submittedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    };

    // Add listener with capture phase to catch it before other handlers
    document.addEventListener('keydown', blockEscKey, true);
    document.addEventListener('keyup', blockEscKey, true);
    document.addEventListener('keypress', blockEscKey, true);

    return () => {
      document.removeEventListener('keydown', blockEscKey, true);
      document.removeEventListener('keyup', blockEscKey, true);
      document.removeEventListener('keypress', blockEscKey, true);
    };
  }, []);

  const fetchExamData = async () => {
    try {
      const studentId = localStorage.getItem('studentId');
      
      // Fetch exam first to check attempt limits
      const examDoc = await getDoc(doc(db, 'exams', examId));
      if (!examDoc.exists()) {
        showError('Exam not found');
        navigate('/student-dashboard');
        return;
      }
      const examData = { id: examDoc.id, ...examDoc.data() };

      // For random selection exams, treat selected subjects as exam sections so UI can segregate questions
      // (Biology section shows only Biology questions, etc.)
      if (examData.useRandomSelection && Array.isArray(examData.selectedSubjects) && examData.selectedSubjects.length > 0) {
        const derivedSections = examData.selectedSubjects.map((subjectName) => ({
          name: subjectName,
          // questions/duration are not required for random selection display; keep for compatibility
          questions: '',
          duration: ''
        }));

        examData.sections = derivedSections;
        examData.numberOfSections = derivedSections.length;
      }
      
      // Check how many completed attempts this student has
      const completedSubmissionsQuery = query(
        collection(db, 'examSubmissions'),
        where('examId', '==', examId),
        where('studentId', '==', studentId),
        where('status', '==', 'completed')
      );
      const completedSnapshot = await getDocs(completedSubmissionsQuery);
      const completedAttempts = completedSnapshot.size;
      
      // Determine maximum allowed attempts
      const maxAttempts = examData.attemptLimit === 'unlimited' 
        ? Infinity 
        : examData.attemptLimit === 'custom'
        ? parseInt(examData.customAttempts)
        : examData.attemptLimit === 'once'
        ? 1
        : parseInt(examData.attemptLimit);
      
      // CRITICAL: Block access if max attempts exceeded
      if (completedAttempts >= maxAttempts && maxAttempts !== Infinity) {
        console.log('Maximum attempts exceeded:', completedAttempts, '/', maxAttempts);
        showError(`You have already used all ${maxAttempts} attempt(s) for this exam!`);
        
        // Redirect to last submission result if available
        if (!completedSnapshot.empty && examData.showResultImmediately) {
          const lastSubmission = completedSnapshot.docs[completedSnapshot.docs.length - 1];
          navigate(`/exam-result/${lastSubmission.id}`, { replace: true });
        } else {
          navigate('/student-dashboard', { replace: true });
        }
        return;
      }
      
      // If we reach here, student can take/retake the exam
      if (completedAttempts > 0) {
        console.log(`Student has ${completedAttempts} completed attempt(s), ${maxAttempts - completedAttempts} remaining`);
        showInfo(`This is attempt ${completedAttempts + 1} of ${maxAttempts === Infinity ? 'unlimited' : maxAttempts}`);
      }
      
      setExam(examData);

      // Determine timer type
      let detectedTimerType = 'main';
      if (examData.hasPerQuestionTimer && examData.timePerQuestion) {
        detectedTimerType = 'question';
        const timePerQInSeconds = parseInt(examData.timePerQuestion);
        setQuestionTimeLeft(timePerQInSeconds);
        setTimeLeft(parseInt(examData.duration) * 60); // Main timer still runs in background
      } else if (examData.sections && examData.sections.length > 1) {
        // Check if sections have individual durations
        const hasSectionDurations = examData.sections.some(s => s.duration && parseFloat(s.duration) > 0);
        if (hasSectionDurations) {
          detectedTimerType = 'section';
          const firstSectionDuration = parseFloat(examData.sections[0].duration || 0) * 60;
          setSectionTimeLeft(firstSectionDuration);
          setTimeLeft(parseInt(examData.duration) * 60);
        } else {
          detectedTimerType = 'main';
          setTimeLeft(parseInt(examData.duration) * 60);
        }
      } else {
        detectedTimerType = 'main';
        setTimeLeft(parseInt(examData.duration) * 60);
      }
      
      setTimerType(detectedTimerType);

      // Fetch questions
      let questionsList = [];
      
      // Check if exam uses random selection mode
      if (examData.useRandomSelection && examData.selectedSubjects && examData.selectedSubjects.length > 0) {
        // Random selection mode: fetch from question library
        const adminId = examData.organizationId;
        const selectedSubjects = examData.selectedSubjects;
        const totalQuestions = parseInt(examData.totalQuestions);
        const questionsPerSubject = Math.floor(totalQuestions / selectedSubjects.length);
        const extraQuestions = totalQuestions % selectedSubjects.length;
        
        // Generate seed for random selection based on student and exam
        const seed = generateSeed(studentId, examId);
        
        // Fetch questions from each selected subject
        for (let subjectIndex = 0; subjectIndex < selectedSubjects.length; subjectIndex++) {
          const subject = selectedSubjects[subjectIndex];
          
          // Fetch all questions for this subject from question library
          const libQuery = query(
            collection(db, 'questionLibrary'),
            where('organizationId', '==', adminId),
            where('subjectName', '==', subject)
          );
          const libSnapshot = await getDocs(libQuery);
          const subjectQuestions = [];
          libSnapshot.forEach((doc) => {
            subjectQuestions.push({ id: doc.id, ...doc.data() });
          });
          
          // Determine how many questions to select from this subject
          let questionsToSelect = questionsPerSubject;
          if (subjectIndex === 0) {
            questionsToSelect += extraQuestions; // Add extra questions to first subject
          }
          
          // Randomly select questions using seed
          const subjectSeed = seed + subjectIndex * 1000;
          const shuffledSubjectQuestions = shuffleArray(subjectQuestions, subjectSeed);
          const selectedQuestions = shuffledSubjectQuestions.slice(0, Math.min(questionsToSelect, shuffledSubjectQuestions.length));
          
          // Add section info for display
          selectedQuestions.forEach(q => {
            q.sectionId = subjectIndex;
            q.sectionName = subject;
          });
          
          questionsList.push(...selectedQuestions);
        }
        
        if (questionsList.length === 0) {
          showError('No questions available in the question library for the selected subjects');
          navigate('/student-dashboard');
          return;
        }
        
        if (questionsList.length < totalQuestions) {
          showWarning(`Only ${questionsList.length} questions available (requested ${totalQuestions})`);
        }
      } else {
        // Normal mode: fetch questions assigned to exam
        const questionsQuery = query(
          collection(db, 'questions'),
          where('examId', '==', examId)
        );
        const questionsSnapshot = await getDocs(questionsQuery);
        questionsSnapshot.forEach((doc) => {
          questionsList.push({ id: doc.id, ...doc.data() });
        });

        if (questionsList.length === 0) {
          showError('No questions found for this exam');
          navigate('/student-dashboard');
          return;
        }
      }

      // Generate seed for shuffling based on student and exam (studentId already declared at top)
      const seed = generateSeed(studentId, examId);
      
      // Shuffle questions and create mapping
      const shuffleMap = {
        sectionOrder: [],
        questionOrder: {},
        optionOrder: {},
        originalToShuffled: {},
        shuffledToOriginal: {},
        displaySectionMap: {} // Maps displaySectionIndex to original section data
      };

      let shuffledQuestionsList = [];
      const displayMap = {};
      const orderedIndices = [];
      let displayNumber = 1;
      
      if (examData.sections && examData.sections.length > 1) {
        // Shuffle section order per student (works for both manual sections and random-selection subjects)
        // This still keeps questions segregated by their subject/section.
        const sectionIndices = examData.sections.map((_, idx) => idx);
        const shuffledSectionIndices = shuffleArray(sectionIndices, seed);
        shuffleMap.sectionOrder = shuffledSectionIndices;
        
        // For each shuffled section, shuffle questions within it
        shuffledSectionIndices.forEach((originalSectionIndex, displaySectionIndex) => {
          // Store the mapping of display position to original section
          shuffleMap.displaySectionMap[displaySectionIndex] = {
            originalIndex: originalSectionIndex,
            name: examData.sections[originalSectionIndex].name,
            duration: examData.sections[originalSectionIndex].duration
          };
          
          // Get questions for this original section
          const sectionQuestions = questionsList
            .map((q, idx) => ({ question: q, originalIndex: idx }))
            .filter(item => (item.question.sectionId || 0) === originalSectionIndex);
          
          // Shuffle questions within section
          const sectionSeed = seed + originalSectionIndex * 1000;
          const shuffledSectionQuestions = shuffleArray(sectionQuestions, sectionSeed);
          
          // Store question order for this section
          shuffleMap.questionOrder[originalSectionIndex] = shuffledSectionQuestions.map(item => item.originalIndex);
          
          // Assign display numbers and shuffle options
          shuffledSectionQuestions.forEach(({ question, originalIndex }) => {
            // Shuffle options for this question
            if (question.questionType === 'mcq' && question.options && question.options.length > 0) {
              const optionSeed = seed + originalIndex * 100;
              
              // Add originalIndex to each option
              const optionsWithIndices = question.options.map((opt, idx) => ({
                ...opt,
                originalIndex: idx
              }));
              
              const shuffledOptions = shuffleArray(optionsWithIndices, optionSeed);
              
              // Store option shuffle mapping
              shuffleMap.optionOrder[question.id] = shuffledOptions.map(opt => opt.originalIndex);
              
              // Create shuffled question with shuffled options and NEW sectionId
              question = { 
                ...question, 
                options: shuffledOptions,
                originalSectionId: question.sectionId,
                sectionId: displaySectionIndex,  // Assign new visual section index
                sectionName: examData.sections[originalSectionIndex].name // Store original section name
              };
            } else {
              // For non-MCQ questions, still update the sectionId
              question = {
                ...question,
                originalSectionId: question.sectionId,
                sectionId: displaySectionIndex,
                sectionName: examData.sections[originalSectionIndex].name
              };
            }
            
            // Map display positions - shuffled index maps to sequential display number
            const shuffledIndex = shuffledQuestionsList.length;
            displayMap[shuffledIndex] = displayNumber;
            shuffleMap.originalToShuffled[originalIndex] = shuffledIndex;
            shuffleMap.shuffledToOriginal[shuffledIndex] = originalIndex;
            
            orderedIndices.push(shuffledIndex);
            shuffledQuestionsList.push(question);
            displayNumber++;
          });
        });
      } else {
        // No sections: shuffle all questions
        const questionsWithIndices = questionsList.map((q, idx) => ({ question: q, originalIndex: idx }));
        const shuffledQuestionObjects = shuffleArray(questionsWithIndices, seed);
        
        shuffleMap.questionOrder[0] = shuffledQuestionObjects.map(item => item.originalIndex);
        
        shuffledQuestionObjects.forEach(({ question, originalIndex }) => {
          // Shuffle options for this question
          if (question.questionType === 'mcq' && question.options && question.options.length > 0) {
            const optionSeed = seed + originalIndex * 100;
            
            // Add originalIndex to each option
            const optionsWithIndices = question.options.map((opt, idx) => ({
              ...opt,
              originalIndex: idx
            }));
            
            const shuffledOptions = shuffleArray(optionsWithIndices, optionSeed);
            
            // Store option shuffle mapping
            shuffleMap.optionOrder[question.id] = shuffledOptions.map(opt => opt.originalIndex);
            
            question = { ...question, options: shuffledOptions };
          }
          
          // Map display positions - shuffled index maps to sequential display number
          const shuffledIndex = shuffledQuestionsList.length;
          displayMap[shuffledIndex] = displayNumber;
          shuffleMap.originalToShuffled[originalIndex] = shuffledIndex;
          shuffleMap.shuffledToOriginal[shuffledIndex] = originalIndex;
          
          orderedIndices.push(shuffledIndex);
          shuffledQuestionsList.push(question);
          displayNumber++;
        });
      }
      
      // Set the shuffled questions and mapping
      setQuestions(shuffledQuestionsList);
      setQuestionDisplayMap(displayMap);
      setDisplayOrder(orderedIndices);
      setShuffleMapping(shuffleMap);
      
      // Update exam with shuffled section order
      if (examData.sections && examData.sections.length > 1 && shuffleMap.displaySectionMap) {
        const shuffledSections = Object.keys(shuffleMap.displaySectionMap).map(displayIdx => {
          const displayIndex = parseInt(displayIdx);
          const sectionInfo = shuffleMap.displaySectionMap[displayIndex];
          return {
            ...examData.sections[sectionInfo.originalIndex],
            displayIndex: displayIndex,
            originalIndex: sectionInfo.originalIndex
          };
        });
        setExam({ ...examData, shuffledSections });
        
        // Update section timer if using section-based timing
        if (detectedTimerType === 'section') {
          const firstShuffledSectionDuration = parseFloat(shuffledSections[0].duration || 0) * 60;
          setSectionTimeLeft(firstShuffledSectionDuration);
        }
      }
      
      // Set the first question based on shuffled order
      if (shuffledQuestionsList.length > 0) {
        setCurrentQuestionIndex(0);
        setVisited(new Set([0]));
        
        // Set current section based on first question
        const firstQuestion = shuffledQuestionsList[0];
        if (firstQuestion && firstQuestion.sectionId !== undefined) {
          setCurrentSection(firstQuestion.sectionId || 0);
        }
      }

      console.log('Shuffle Mapping:', shuffleMap);
      console.log('Display Map:', displayMap);
      console.log('Shuffled Questions:', shuffledQuestionsList.length);

      // Create initial submission record with security data
      const studentName = localStorage.getItem('studentName');
      const submissionData = {
        examId: examId,
        examName: examData.name,
        studentId: studentId,
        studentName: studentName,
        studentEmail: localStorage.getItem('studentEmail'),
        organizationId: localStorage.getItem('studentOrganizationId') || null,
        startTime: new Date().toISOString(),
        status: 'in-progress',
        answers: {},
        tabSwitchCount: 0,
        cameraPermission: cameraPermission,
        locationPermission: locationPermission,
        userLocation: userLocation,
        timerType: detectedTimerType,
        shuffleMapping: shuffleMap,
        createdAt: new Date().toISOString()
      };

      const submissionRef = await addDoc(collection(db, 'examSubmissions'), submissionData);
      setSubmissionId(submissionRef.id);
      console.log('Submission record created with ID:', submissionRef.id);

    } catch (error) {
      console.error('Error fetching exam data:', error);
      showError('Failed to load exam');
      navigate('/student-dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Define submitExam first
  const submitExam = useCallback(async (autoSubmit = false) => {
    if (submitting || submittedRef.current) return;
    
    // Check if submissionId exists
    if (!submissionId) {
      console.error('No submission ID found');
      showError('Cannot submit exam - no submission record found');
      return;
    }
    
    setSubmitting(true);
    submittedRef.current = true;

    try {
      console.log('Starting exam submission...', { submissionId, autoSubmit });
      
      // Calculate score
      let totalScore = 0;
      const questionResults = {};
      const hasNegativeMarking = exam?.hasNegativeMarking || false;
      const negativeMarksValue = parseFloat(exam?.negativeMarks) || 0;

      questions.forEach(question => {
        const studentAnswer = answers[question.id];
        let isCorrect = false;
        let scoredMarks = 0;

        if (question.questionType === 'mcq') {
          // For MCQ, we need to handle the comparison properly
          // The studentAnswer might be the option text or generated value like "option_0_img"
          // We'll check correctness in the next section
        } else {
          // For subjective questions
          isCorrect = studentAnswer?.toLowerCase().trim() === question.correctAnswer?.toLowerCase().trim();
          if (isCorrect) {
            scoredMarks = question.marks;
            totalScore += question.marks;
          } else if (studentAnswer && studentAnswer !== '' && hasNegativeMarking) {
            scoredMarks = -negativeMarksValue;
            totalScore -= negativeMarksValue;
          }
        }

        // Get correct answer, ensuring it's never undefined
        let correctAnswerText = 'Not Available';
        let correctAnswerValue = null;
        let correctAnswerImage = null;
        if (question.questionType === 'mcq') {
          const correctOption = question.options?.find(opt => opt.isCorrect);
          if (correctOption) {
            // IMPORTANT: Use originalIndex if available (for shuffled options)
            // This ensures we match against the same index used when storing the answer
            const correctOriginalIndex = correctOption.originalIndex !== undefined 
              ? correctOption.originalIndex 
              : question.options?.findIndex(opt => opt.isCorrect);
            
            console.log('Correct option details:', {
              questionId: question.id,
              correctOption,
              correctOriginalIndex,
              hasOriginalIndex: correctOption.originalIndex !== undefined
            });
            
            // Use same logic as QuestionDisplay for option value
            correctAnswerValue = correctOption.text || `option_${correctOriginalIndex}_${correctOption.image ? 'img' : 'txt'}`;
            correctAnswerText = correctOption.text || `Option ${correctOriginalIndex + 1} (Image)`;
            correctAnswerImage = correctOption.image || null;
          } else {
            correctAnswerText = 'Not Available';
          }
        } else {
          correctAnswerText = question.correctAnswer || 'Not Available';
          correctAnswerValue = question.correctAnswer;
        }

        // For MCQ, compare with the correct option value
        if (question.questionType === 'mcq' && correctAnswerValue) {
          isCorrect = studentAnswer === correctAnswerValue;
          console.log('MCQ Answer Comparison:', {
            questionId: question.id,
            studentAnswer,
            correctAnswerValue,
            isCorrect
          });
          if (isCorrect) {
            scoredMarks = question.marks;
            totalScore += question.marks;
          } else if (studentAnswer && studentAnswer !== '' && hasNegativeMarking) {
            scoredMarks = -negativeMarksValue;
            totalScore -= negativeMarksValue;
          }
        }

        // Determine if question was answered (must have a truthy, non-empty answer)
        // For image-only options, the value will be like "option_0_img" which is valid
        const wasAnswered = !!(studentAnswer && studentAnswer !== '' && studentAnswer !== null && studentAnswer !== undefined);

        console.log('Question Result:', {
          questionId: question.id,
          studentAnswer,
          wasAnswered,
          isCorrect,
          scoredMarks
        });

        // Round scoredMarks to 2 decimal places
        const roundedScoredMarks = Math.round(scoredMarks * 100) / 100;

        // Format student answer for display
        let displayStudentAnswer = studentAnswer || 'Not Answered';
        let studentAnswerImage = null;
        if (question.questionType === 'mcq' && studentAnswer) {
          if (studentAnswer.startsWith('option_')) {
            // Extract option number from "option_0_img" format
            const match = studentAnswer.match(/option_(\d+)_/);
            if (match) {
              const originalOptionIndex = parseInt(match[1]);
              // Find the option with this originalIndex
              // For shuffled questions, options will have originalIndex property
              const selectedOption = question.options?.find(opt => {
                if (opt.originalIndex !== undefined) {
                  return opt.originalIndex === originalOptionIndex;
                }
                // Fallback for non-shuffled questions
                return question.options?.indexOf(opt) === originalOptionIndex;
              });
              
              console.log('Formatting student answer:', {
                questionId: question.id,
                studentAnswer,
                originalOptionIndex,
                selectedOption,
                allOptions: question.options?.map(o => ({ text: o.text, originalIndex: o.originalIndex, isCorrect: o.isCorrect }))
              });
              
              if (selectedOption) {
                displayStudentAnswer = selectedOption.text || `Option ${originalOptionIndex + 1} (Image)`;
                studentAnswerImage = selectedOption.image || null;
              } else {
                displayStudentAnswer = `Option ${originalOptionIndex + 1} (Image)`;
              }
            }
          }
          // If studentAnswer is text (not option_X format), it's already the text
        }

        questionResults[question.id] = {
          questionText: question.questionText || 'Question',
          studentAnswer: displayStudentAnswer,
          studentAnswerImage: studentAnswerImage,
          correctAnswer: correctAnswerText,
          correctAnswerImage: correctAnswerImage,
          marks: question.marks || 0,
          scored: roundedScoredMarks,
          isCorrect: isCorrect,
          wasAnswered: wasAnswered
        };
      });

      console.log('Final Answers State:', answers);
      console.log('Question Results:', questionResults);

      // Verify counts
      const questionsArray = Object.values(questionResults);
      const correctCount = questionsArray.filter(q => q.isCorrect).length;
      const wrongCount = questionsArray.filter(q => q.wasAnswered && !q.isCorrect).length;
      const unansweredCount = questionsArray.filter(q => !q.wasAnswered).length;
      console.log('Result Summary:', {
        total: questionsArray.length,
        correct: correctCount,
        wrong: wrongCount,
        unanswered: unansweredCount,
        totalScore,
        totalMarks: exam.totalMarks
      });

      // Update submission with security data
      console.log('Updating submission document...', submissionId);
      
      // Round totalScore to 2 decimal places to avoid floating-point precision issues
      const roundedTotalScore = Math.round(totalScore * 100) / 100;
      const roundedPercentage = Math.round((totalScore / exam.totalMarks) * 100 * 10) / 10;
      
      await updateDoc(doc(db, 'examSubmissions', submissionId), {
        answers: answers,
        questionResults: questionResults,
        totalScore: roundedTotalScore,
        totalMarks: exam.totalMarks,
        percentage: roundedPercentage,
        endTime: new Date().toISOString(),
        status: 'completed',
        submittedAt: new Date().toISOString(),
        autoSubmitted: autoSubmit,
        tabSwitchCount: tabSwitchCount,
        cameraPermission: cameraPermission,
        locationPermission: locationPermission,
        userLocation: userLocation,
        markedForReviewCount: markedForReview.size,
        timerType: timerType,
        questionTimeTracking: questionTimeRemaining
      });

      console.log('Exam submitted successfully');
      showSuccess('Exam submitted successfully!');
      
      // Exit fullscreen
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(console.error);
      }
      
      // Use replace to prevent back navigation to exam
      setTimeout(() => {
        if (exam?.showResultImmediately) {
          navigate(`/exam-result/${submissionId}`, { replace: true });
        } else {
          showInfo('Results will be available once published by your instructor');
          navigate('/student-dashboard', { replace: true });
        }
      }, 1500);

    } catch (error) {
      console.error('Error submitting exam:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        submissionId,
        hasExam: !!exam,
        hasQuestions: questions.length > 0
      });
      showError(`Failed to submit exam: ${error.message || 'Please try again'}`);
      submittedRef.current = false;
      setSubmitting(false);
    }
  }, [submitting, answers, questions, exam, submissionId, tabSwitchCount, cameraPermission, locationPermission, userLocation, markedForReview, timerType, questionTimeRemaining, navigate]);

  // Immediate save function - no delay (defined early for use in callbacks)
  const saveAnswersImmediately = useCallback(async () => {
    if (!submissionId) return;
    
    // Use answersRef to get the most current state
    const currentAnswers = answersRef.current;
    
    try {
      await updateDoc(doc(db, 'examSubmissions', submissionId), {
        answers: currentAnswers,
        updatedAt: new Date().toISOString()
      });
      console.log('Answers saved immediately:', currentAnswers);
    } catch (error) {
      console.error('Immediate save failed:', error);
    }
  }, [submissionId]);

  // Define handleAutoSubmit with proper dependencies
  const handleAutoSubmit = useCallback(async () => {
    if (submittedRef.current || submitting) {
      console.log('Auto-submit already in progress or completed, skipping');
      return;
    }
    
    console.log('Auto-submit triggered - Time expired');
    showWarning('Time is up! Auto-submitting exam...');
    await submitExam(true);
  }, [submitExam, submitting]);

  // Define goToQuestion
  const goToQuestion = useCallback(async (index) => {
    console.log('goToQuestion called with index:', index);
    
    // Prevent going back to expired questions
    if (timerType === 'question' && expiredQuestions.has(index)) {
      console.log('goToQuestion - Question expired, cannot navigate');
      showWarning('Cannot go back to a question whose time has expired!');
      return;
    }
    
    console.log('goToQuestion - Saving answers before navigation');
    // Save current answers immediately before navigating
    await saveAnswersImmediately();
    
    console.log('goToQuestion - Setting current question index to:', index);
    setCurrentQuestionIndex(index);
    setVisited(prev => new Set([...prev, index]));
    
    // Restore question timer from saved time or start fresh
    if (timerType === 'question' && exam?.timePerQuestion) {
      const question = questions[index];
      const savedTime = questionTimeRemaining[question.id];
      
      if (savedTime !== undefined && savedTime > 0) {
        // Resume from saved time
        console.log('goToQuestion - Restoring saved time:', savedTime);
        setQuestionTimeLeft(savedTime);
      } else {
        // Start fresh timer
        const timePerQInSeconds = parseInt(exam.timePerQuestion);
        console.log('goToQuestion - Starting fresh timer:', timePerQInSeconds);
        setQuestionTimeLeft(timePerQInSeconds);
      }
    }
    console.log('goToQuestion - Navigation complete');
  }, [timerType, expiredQuestions, exam, questions, questionTimeRemaining, saveAnswersImmediately]);

  // Main Timer countdown
  useEffect(() => {
    if (timeLeft <= 0 || !exam || submittedRef.current) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          if (timerType === 'main' && !submittedRef.current) {
            console.log('Main timer expired, calling handleAutoSubmit');
            handleAutoSubmit();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, exam, timerType, handleAutoSubmit]);

  // Question Timer countdown - Track and restore time per question
  useEffect(() => {
    if (timerType !== 'question' || questionTimeLeft <= 0 || !exam) return;

    const timer = setInterval(() => {
      setQuestionTimeLeft(prev => {
        const newTime = prev - 1;
        
        // Save current question's remaining time
        const currentQ = questions[currentQuestionIndex];
        if (currentQ) {
          setQuestionTimeRemaining(prevTimes => ({
            ...prevTimes,
            [currentQ.id]: newTime
          }));
        }
        
        if (newTime <= 0) {
          clearInterval(timer);
          // Mark question as expired
          setExpiredQuestions(prev => new Set([...prev, currentQuestionIndex]));
          
          // Save answers immediately before moving to next question
          saveAnswersImmediately();
          
          // Auto-save and move to next question in display order
          const currentPositionInDisplay = displayOrder.indexOf(currentQuestionIndex);
          if (currentPositionInDisplay < displayOrder.length - 1) {
            showWarning('Time up for this question! Moving to next...');
            setTimeout(() => {
              const nextIndex = displayOrder[currentPositionInDisplay + 1];
              const nextQ = questions[nextIndex];
              console.log('Question timer expired - Moving from position', currentPositionInDisplay, 'to', currentPositionInDisplay + 1, 'Index:', nextIndex);
              setCurrentQuestionIndex(nextIndex);
              setVisited(prev => new Set([...prev, nextIndex]));
              
              // Load saved time or start fresh
              const savedTime = questionTimeRemaining[nextQ.id];
              if (savedTime !== undefined && savedTime > 0) {
                setQuestionTimeLeft(savedTime);
              } else {
                setQuestionTimeLeft(parseInt(exam.timePerQuestion));
              }
            }, 1000);
          } else {
            // Last question - submit exam
            console.log('Last question timer expired, calling handleAutoSubmit');
            handleAutoSubmit();
          }
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [questionTimeLeft, exam, timerType, currentQuestionIndex, questions, questionTimeRemaining, handleAutoSubmit, displayOrder, saveAnswersImmediately]);

  // Section Timer countdown
  useEffect(() => {
    if (timerType !== 'section' || sectionTimeLeft <= 0 || !exam || !exam.sections) return;

    const timer = setInterval(() => {
      setSectionTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          // Auto-save and move to next section
          const nextSection = currentSection + 1;
          const sectionsArray = exam.shuffledSections || exam.sections;
          if (nextSection < sectionsArray.length) {
            showWarning(`Time up for Section ${currentSection + 1}! Moving to next section...`);
            setTimeout(() => {
              setCurrentSection(nextSection);
              const nextSectionDuration = parseFloat(sectionsArray[nextSection].duration || 0) * 60;
              setSectionTimeLeft(nextSectionDuration);
              // Find first question of next section
              const nextSectionFirstQuestion = questions.findIndex(q => q.sectionId === nextSection);
              if (nextSectionFirstQuestion !== -1) {
                goToQuestion(nextSectionFirstQuestion);
              }
            }, 1000);
          } else {
            // Last section - submit exam
            console.log('Last section timer expired, calling handleAutoSubmit');
            handleAutoSubmit();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [sectionTimeLeft, exam, timerType, currentSection, questions, goToQuestion, handleAutoSubmit]);

  // Auto-save answers with debounce
  useEffect(() => {
    if (!submissionId || Object.keys(answers).length === 0) return;

    const saveTimeout = setTimeout(() => {
      saveAnswers();
    }, 500); // Reduced to 500ms for faster auto-save

    return () => clearTimeout(saveTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]);

  // Sync answersRef with answers state
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  const saveAnswers = async () => {
    if (!submissionId) return;
    
    try {
      await updateDoc(doc(db, 'examSubmissions', submissionId), {
        answers: answers,
        updatedAt: new Date().toISOString()
      });
      console.log('Answers auto-saved successfully');
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    // Update state with callback to ensure we have latest state
    setAnswers(prev => {
      const newAnswers = {
        ...prev,
        [questionId]: answer
      };
      
      // Update ref with latest answers
      answersRef.current = newAnswers;
      
      // Find the question by ID to check its type
      const currentQ = questions.find(q => q.id === questionId);
      
      // For MCQ questions, save immediately with the new answer
      // For subjective questions, rely on debounced auto-save
      if (currentQ && currentQ.questionType === 'mcq' && submissionId) {
        // Save immediately with the updated answers object
        updateDoc(doc(db, 'examSubmissions', submissionId), {
          answers: newAnswers,
          updatedAt: new Date().toISOString()
        }).then(() => {
          console.log('MCQ Answer saved immediately for question:', questionId, 'Answer:', answer);
        }).catch((error) => {
          console.error('Immediate save failed:', error);
        });
      }
      
      return newAnswers;
    });
  };

  const toggleMarkForReview = (index) => {
    setMarkedForReview(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleNext = async () => {
    console.log('handleNext called - START');
    if (displayOrder.length === 0) {
      console.log('handleNext - displayOrder is empty, returning');
      return;
    }
    
    const currentPositionInDisplay = displayOrder.indexOf(currentQuestionIndex);
    console.log('handleNext - Current Index:', currentQuestionIndex, 'Position in Display:', currentPositionInDisplay);
    
    if (currentPositionInDisplay < displayOrder.length - 1) {
      const nextIndex = displayOrder[currentPositionInDisplay + 1];
      console.log('handleNext - Moving to next - Display position:', currentPositionInDisplay + 1, 'Original Index:', nextIndex);
      await goToQuestion(nextIndex);
      console.log('handleNext - Navigation complete');
    } else {
      console.log('handleNext - Already on last question');
    }
  };

  const handlePrevious = async () => {
    console.log('handlePrevious called - START');
    if (displayOrder.length === 0) {
      console.log('handlePrevious - displayOrder is empty, returning');
      return;
    }
    
    const currentPositionInDisplay = displayOrder.indexOf(currentQuestionIndex);
    console.log('handlePrevious - Current Index:', currentQuestionIndex, 'Position in Display:', currentPositionInDisplay);
    
    if (currentPositionInDisplay > 0) {
      const prevIndex = displayOrder[currentPositionInDisplay - 1];
      console.log('handlePrevious - Moving to previous - Display position:', currentPositionInDisplay - 1, 'Original Index:', prevIndex);
      await goToQuestion(prevIndex);
      console.log('handlePrevious - Navigation complete');
    } else {
      console.log('handlePrevious - Already on first question');
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getQuestionStatus = (index) => {
    const question = questions[index];
    const isAnswered = answers[question.id] !== undefined && answers[question.id] !== '';
    const isMarked = markedForReview.has(index);
    const isVisited = visited.has(index);
    const isCurrent = index === currentQuestionIndex;

    if (isCurrent) return 'current';
    if (isAnswered && isMarked) return 'answered-marked';
    if (isAnswered) return 'answered';
    if (isMarked) return 'marked';
    if (isVisited) return 'not-answered';
    return 'not-visited';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'current': return 'bg-indigo-600 text-white border-indigo-600';
      case 'answered': return 'bg-green-500 text-white border-green-600';
      case 'answered-marked': return 'bg-gradient-to-r from-green-500 to-purple-500 text-white border-purple-600';
      case 'marked': return 'bg-purple-500 text-white border-purple-600';
      case 'not-answered': return 'bg-red-500 text-white border-red-600';
      default: return 'bg-gray-200 text-gray-600 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto"></div>
          <p className="text-gray-600 mt-4 font-medium">Loading exam...</p>
        </div>
      </div>
    );
  }

  if (!exam || questions.length === 0) return null;

  const currentQuestion = questions[currentQuestionIndex];
  const answeredCount = Object.keys(answers).filter(key => answers[key] !== undefined && answers[key] !== '').length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Compact */}
      <ExamHeader 
        exam={exam}
        currentQuestionIndex={currentQuestionIndex}
        questions={questions}
        answeredCount={answeredCount}
        isFullscreen={isFullscreen}
        cameraPermission={cameraPermission}
        tabSwitchCount={tabSwitchCount}
        timeLeft={timeLeft}
        timerType={timerType}
        questionTimeLeft={questionTimeLeft}
        sectionTimeLeft={sectionTimeLeft}
        currentSection={currentSection}
        formatTime={formatTime}
      />

      {/* Fullscreen Notice Banner - Shown when not in fullscreen */}
      {!isFullscreen && (
        <div className="bg-red-600 text-white px-4 py-3 shadow-lg sticky top-0 z-50 animate-pulse">
          <div className="max-w-7xl mx-auto flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <svg className="w-6 h-6 flex-shrink-0 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="text-center">
                <p className="font-bold text-sm sm:text-base">⚠️ FULLSCREEN MODE IS MANDATORY!</p>
                <p className="text-xs sm:text-sm font-semibold">Automatically re-entering fullscreen... You cannot exit during the exam!</p>
              </div>
              <svg className="w-6 h-6 flex-shrink-0 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Question Panel */}
          <div className="lg:col-span-3">
            <QuestionDisplay 
              currentQuestion={currentQuestion}
              currentQuestionIndex={currentQuestionIndex}
              exam={exam}
              answers={answers}
              markedForReview={markedForReview}
              handleAnswerChange={handleAnswerChange}
              toggleMarkForReview={toggleMarkForReview}
            />

            {/* Navigation Buttons */}
            <QuestionNavigation 
              currentQuestion={currentQuestion}
              currentQuestionIndex={currentQuestionIndex}
              handlePrevious={handlePrevious}
              handleNext={handleNext}
              toggleMarkForReview={toggleMarkForReview}
              handleAnswerChange={handleAnswerChange}
              setShowSubmitConfirm={setShowSubmitConfirm}
              questions={questions}
            />
          </div>

          {/* Question Palette */}
          <div className="lg:col-span-1">
            <QuestionPalette 
              exam={exam}
              questions={questions}
              answers={answers}
              markedForReview={markedForReview}
              visited={visited}
              currentQuestionIndex={currentQuestionIndex}
              currentQuestion={currentQuestion}
              goToQuestion={goToQuestion}
              setShowSubmitConfirm={setShowSubmitConfirm}
              getQuestionStatus={getQuestionStatus}
              getStatusColor={getStatusColor}
            />
          </div>
        </div>
      </div>

      {/* Submit Confirmation Modal */}
      <SubmitConfirmModal 
        showSubmitConfirm={showSubmitConfirm}
        setShowSubmitConfirm={setShowSubmitConfirm}
        questions={questions}
        answeredCount={answeredCount}
        markedForReview={markedForReview}
        tabSwitchCount={tabSwitchCount}
        submitExam={submitExam}
        submitting={submitting}
      />

      {/* Warning Modal for Tab Switching */}
      <WarningModal 
        showWarningModal={showWarningModal}
        warningMessage={warningMessage}
        tabSwitchCount={tabSwitchCount}
      />

      {/* Hidden camera feed for monitoring (if enabled) */}
      <video 
        ref={videoRef} 
        autoPlay 
        muted 
        className="hidden" 
      />
    </div>
  );
};

export default TakeExam;
